import type { LevelDef } from '../content/levelSchema';
import type { SimLevel } from '../sim/level';
import { MOWER_DEFS, type MowerDef, type MowerId } from '../content/mowers';
import { bestClear, routeTry, solveGreedy } from '../gen/solver/greedyPlanner';
import { upgradeCosts, upgradedMower, upgradeLevels } from './economy';
import type { Save, UpgradeLevels } from './save/schema';

/**
 * Par and gold times (owner, 2026-10-31, late-lawn balance): the fastest clean route the planner
 * finds over several tries, measured on the stock mower up to lawn 100 and, from lawn 101, on the
 * mower with every upgrade that changes a route (Sharpened Blades, Power Steering, Reverse Gear),
 * since late players have them and par measured without them was far too easy. The Tuned Engine
 * is left out here: the goal is scaled by the engine level in use when the lawn is played
 * (economy.ts, `parGoal`). Late lawns also get a gold time, the fastest route when smashing is
 * allowed (par never smashes).
 */

/** From this lawn on, par and gold times assume the route upgrades. */
export const ROUTE_UPGRADES_FROM = 101;

const NONE: UpgradeLevels = {
  tank: 0,
  hp: 0,
  blades: 0,
  engine: 0,
  steering: 0,
  reverse: 0,
  tires: 0,
  bumper: 0,
  reserve: 0,
};

/** The upgrades that change a mower's route. */
export type RouteUpgrade = 'blades' | 'steering' | 'reverse';
export const ROUTE_UPGRADES: readonly RouteUpgrade[] = ['blades', 'steering', 'reverse'];

/** The route upgrade levels a lawn's par assumes for a mower (none before lawn 101). */
export function parUpgrades(id: MowerId, lawn: number): Record<RouteUpgrade, number> {
  if (lawn < ROUTE_UPGRADES_FROM) return { blades: 0, steering: 0, reverse: 0 };
  const all = (kind: RouteUpgrade) => upgradeCosts(id, kind).length;
  return {
    blades: all('blades'),
    steering: all('steering'),
    // (The planner can't turn a wide mower right around; none of them can reverse anyway.)
    reverse: (MOWER_DEFS[id].cutWidth ?? 1) > 1 ? 0 : all('reverse'),
  };
}

/** The mower a lawn's par is measured on: stock, or with every route upgrade from lawn 101. */
export function parMower(id: MowerId, lawn: number): MowerDef {
  return upgradedMower(MOWER_DEFS[id], { ...NONE, ...parUpgrades(id, lawn) });
}

/**
 * The route upgrades a lawn's par assumes that the player's mower lacks (the level card says so:
 * owner, 2026-10-31).
 */
export function parUpgradesLacking(
  save: Readonly<Save>,
  id: MowerId,
  lawn: number,
): RouteUpgrade[] {
  const want = parUpgrades(id, lawn);
  const have = upgradeLevels(save, id);
  return ROUTE_UPGRADES.filter((kind) => have[kind] < want[kind]);
}

/** A campaign lawn that has a gold time: a late one, not a boss (bosses have their own goals). */
export function hasGoldTime(lawn: number): boolean {
  return lawn >= ROUTE_UPGRADES_FROM && lawn % 20 !== 0;
}

export interface ParTimes {
  par: Partial<Record<MowerId, number>>;
  goldTime?: Partial<Record<MowerId, number>>;
  routeTries: NonNullable<LevelDef['routeTries']>;
}

/**
 * A campaign lawn's par (and, late, gold) times for `mowers`, or null when a mower can no longer
 * clear it cleanly. For the bake (tools/bake-levels.ts): every route try for every mower.
 */
export function parTimes(def: LevelDef, lawn: number, mowers: readonly MowerId[]): ParTimes | null {
  const par: Partial<Record<MowerId, number>> = {};
  const parTries: Partial<Record<MowerId, number>> = {};
  const gold: Partial<Record<MowerId, number>> = {};
  const goldTries: Partial<Record<MowerId, number>> = {};
  for (const id of mowers) {
    const mower = parMower(id, lawn);
    const clean = bestClear(def.sim, mower);
    if (!clean) return null;
    par[id] = clean.ticks;
    parTries[id] = clean.try;
    if (!hasGoldTime(lawn)) continue;
    const fast = bestClear(def.sim, mower, { smash: true }) ?? clean;
    gold[id] = fast.ticks;
    goldTries[id] = fast.try;
  }
  return hasGoldTime(lawn)
    ? { par, goldTime: gold, routeTries: { par: parTries, gold: goldTries } }
    : { par, routeTries: { par: parTries } };
}

/** Replays one recorded route try: its ticks, or null if it no longer clears cleanly (tests). */
export function replayRouteTry(
  sim: SimLevel,
  lawn: number,
  id: MowerId,
  tryIndex: number,
): number | null {
  const r = solveGreedy(sim, parMower(id, lawn), { planner: routeTry(tryIndex) });
  return r.won && r.crashes === 0 && r.flats === 0 ? r.ticks : null;
}
